import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { UserProfile, UserProfilePatch } from "../models/userProfile.model";
import { SendPhoneVerificationCodeRequest } from "../models/phoneVerification.model";
import { UserImpersonationModel } from "../models/userImpersonation.model";
import { catchError, shareReplay, tap } from "rxjs/operators";
import { Observable, of, throwError } from "rxjs";
import { TimeZoneModel } from "../models/timezone.model";
import { UserUpdateModel } from "../models/userUpdateModel";
import { Enum } from "../models/enum.model";
import { GridService } from "../shared/grid/grid.service";
import { GridModel } from "../shared/grid/grid.model";
import { ToastService } from "./toast.service";
import { Lookup } from "../models/lookup.model";
import { User } from "../models/user.model";
import {
    FlowrateUnit,
    GasUnit,
    PmUnit,
    RainfallUnit,
    TemperatureUnit,
    WindspeedUnit,
} from "../enums/unitPreferences.enum";
import { DatePreference } from "../enums/timePreference.enum";
import { getEnumList } from "../_helpers/enum-helper";
import { ColumnDetails } from "../models/columnDetails.model";
import {
    SetUserStatusRequest,
    UnlockUserAccountRequest,
    UserAccountStatusResponse,
} from "../models/userAccountStatus.model";
import { switchMap } from "rxjs/operators";

@Injectable({
    providedIn: "root",
})
export class UserService extends GridService<User> {
    private cache$: Observable<Lookup[]> | null = null;

    constructor(
        http: HttpClient,
        private toastService: ToastService,
    ) {
        super("User", http);
    }

    get(
        filter: string,
        orderBy: string,
        sort: string,
        page: number,
        pageSize: number,
        filterBy: string,
    ) {
        return this.http.get<GridModel<User>>(this.getApiUrl("User/GetUsers"), {
            params: this.createParamsWithFilterBy(
                filter,
                orderBy,
                sort,
                page,
                pageSize,
                filterBy,
            ),
        });
    }

    getFieldConfiguration(): Observable<{ [property: string]: ColumnDetails }> {
        return this.http
            .get<{
                [property: string]: ColumnDetails;
            }>(this.getApiUrl("User/GetFieldConfiguration"))
            .pipe(
                catchError((error) => {
                    console.error("Error loading field configuration:", error);
                    return of({});
                }),
            );
    }

    update(user: UserUpdateModel) {
        return this.http
            .put(this.getApiUrl("User/UpdateUser"), user)
            .pipe(tap(() => this.clearCache()));
    }

    getUserName(userId: string) {
        return this.http
            .get<string>(this.getApiUrl(`User/GetUserName?userId=${userId}`))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading username",
                        true,
                    );
                    return of("N/A");
                }),
            );
    }

    getSecurityProfileLookupList() {
        return this.http
            .get<Enum[]>(this.getApiUrl("User/GetSecurityProfileLookupList"))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading security profiles",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getAllTimeZones() {
        return this.http
            .get<TimeZoneModel[]>(this.getApiUrl("User/GetAllTimeZones"))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading time zones",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getCurrentUserProfile() {
        return this.http
            .get<UserProfile>(this.getApiUrl("User/GetCurrentUserProfile"))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading user profile",
                        true,
                    );
                    return of(new UserProfile());
                }),
            );
    }

    updateCurrentUserProfile(user: UserProfile) {
        return this.http
            .put(this.getApiUrl("User/UpdateCurrentUserProfile"), user)
            .pipe(tap(() => this.clearCache()));
    }

    patch(field: UserProfilePatch) {
        return this.http
            .patch(this.getApiUrl("User/PatchCurrentUserProfile"), field)
            .pipe(tap(() => this.clearCache()));
    }

    patchUserPreferences(field: any) {
        return this.http
            .patch(this.getApiUrl("User/PatchUserPreferences"), field)
            .pipe(tap(() => this.clearCache()));
    }

    getUserImpersonationList(id: string) {
        return this.http.get<UserImpersonationModel[]>(
            this.getApiUrl(`User/GetUserImpersonationList?tenantId=${id}`),
        );
    }

    getNotificationPreferences() {
        return this.http
            .get<Enum[]>(this.getApiUrl("User/GetNotificationPreferences"))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading notification preferences",
                        true,
                    );
                    return of([]);
                }),
            );
    }
    getTenantLogoUrl(): Observable<string> {
        return this.http.get<string>(this.getApiUrl(`User/GetTenantLogoUrl`));
    }

    sendPhoneVerificationCode(
        consent: SendPhoneVerificationCodeRequest,
    ): Observable<void> {
        return this.http.post<void>(
            this.getApiUrl("User/SendPhoneVerificationCode"),
            consent,
        );
    }

    verifyPhoneCode(code: string): Observable<void> {
        return this.http.post<void>(this.getApiUrl("User/VerifyPhoneCode"), {
            code,
        });
    }

    getById(userId: string): Observable<User> {
        return this.http
            .get<User>(this.getApiUrl(`User/GetUser?userId=${userId}`))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading user",
                        true,
                    );
                    return of(new User());
                }),
            );
    }

    getAccountSecurityById(userId: string): Observable<User> {
        return this.http
            .get<User>(this.getApiUrl(`User/GetUser?userId=${userId}`))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading user",
                        true,
                    );
                    return throwError(() => error);
                }),
            );
    }

    create(payload: User): Observable<User> {
        return this.http
            .post<User>(this.getApiUrl("User/CreateUser"), payload)
            .pipe(
                tap(() => this.clearCache()),
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error creating user",
                        true,
                    );
                    return throwError(() => error);
                }),
            );
    }

    getAll(): Observable<Lookup[]> {
        if (!this.cache$) {
            this.cache$ = this.http
                .get<Lookup[]>(this.getApiUrl(`User/GetUserLookupList`))
                .pipe(
                    catchError((error) => {
                        this.cache$ = null;
                        this.toastService.displayErrorMessage(
                            "Error loading all users",
                            true,
                        );
                        return of([] as Lookup[]);
                    }),
                    shareReplay({ bufferSize: 1, refCount: true }),
                );
        }
        return this.cache$;
    }

    clearCache(): void {
        this.cache$ = null;
    }

    getAllDatePreferences() {
        return of(getEnumList(DatePreference));
    }

    getAllUnitPreferences() {
        return of({
            gasUnits: getEnumList(GasUnit),
            windSpeedUnits: getEnumList(WindspeedUnit),
            rainfallUnits: getEnumList(RainfallUnit),
            flowrateUnits: getEnumList(FlowrateUnit),
            pmUnits: getEnumList(PmUnit),
            temperatureUnits: getEnumList(TemperatureUnit),
        });
    }

    disableUserAccount(userId: string): Observable<boolean> {
        return this.postUserStatus({ userId, status: 1 });
    }

    enableUserAccount(userId: string): Observable<boolean> {
        return this.postUserStatus({ userId, status: 0 });
    }

    setUserAccountStatus(userId: string, status: number): Observable<boolean> {
        return this.postUserStatus({ userId, status });
    }

    unlockUserAccount(userId: string): Observable<boolean> {
        const request: UnlockUserAccountRequest = { userId };
        return this.http
            .post<UserAccountStatusResponse>(
                this.getApiUrl("User/UnlockUserAccount"),
                request,
            )
            .pipe(
                switchMap((response) => this.ensureOperationSucceeded(response)),
                tap(() => this.clearCache()),
            );
    }

    private postUserStatus(request: SetUserStatusRequest): Observable<boolean> {
        return this.http
            .post<UserAccountStatusResponse>(
                this.getApiUrl("User/SetUserStatus"),
                request,
            )
            .pipe(
                switchMap((response) => this.ensureOperationSucceeded(response)),
                tap(() => this.clearCache()),
            );
    }

    private ensureOperationSucceeded(
        response: UserAccountStatusResponse,
    ): Observable<boolean> {
        if (response?.success) {
            return of(true);
        }

        return throwError(
            () => new Error(response?.message || "An error has occurred"),
        );
    }
}
